<script setup lang="ts">
import { useLayoutStore } from "~/composables/stores/layout";
import { PortfolioHeaderDocument, type AssetManagementPortfolio } from "~/graphql/generated/graphql";
import { useQuery } from "@vue/apollo-composable";

const route = useRoute();
const t = useTranslator();
const portfolioId = computed(() => route.params.portfolioId as string);

const filter = useFilter();

const headerQuery = useQuery(PortfolioHeaderDocument, () => ({ id: portfolioId.value }));

const layoutStore = useLayoutStore();

const header = computed<AssetManagementPortfolio | undefined>(() => headerQuery.result.value?.assetManagementPortfolio as AssetManagementPortfolio);
const headerLoading = computed(() => headerQuery.loading.value);
const tabs = useUrlTabsPortfolio();

usePageTitle({ position: 1, title: () => header.value?.name });

watch(
  () => header.value,
  () => {
    if (header.value?.name) {
      useBreadcrumbs().setDynamicLabel("portfolioId", header.value.name);
    }
  },
  { immediate: true }
);
</script>

<template>
  <div :class="$style.container">
    <Composition>
      <template #layouts>
        <CompositionLayoutSingle omitPadding>
          <template #container>
            <Header v-if="!headerLoading" :portfolio="header" colorBackground="--color-accent-100" :colorForeground="'--color-primary'" />
          </template>
        </CompositionLayoutSingle>
      </template>
    </Composition>

    <Composition>
      <template #layouts>
        <CompositionLayoutSingle omitPaddingTop>
          <template #container>
            <WidgetLayout outline>
              <template #widgetRows>
                <WidgetLayoutRowCellSingle>
                  <template #widget>
                    <WidgetBlank colorBackground="--color-white" colorForeground="--color-primary" omitPaddingContent omitPaddingTop omitClickMore>
                      <template #container>
                        <EntityCard
                          v-if="!headerLoading && !!header"
                          colorBackground="--color-white"
                          colorForeground="--color-primary"
                          colorSeperators="--color-accent-200"
                        >
                          <template #header>
                            <KeyValuePair direction="horizontal">
                              <template #containerStart>
                                <Icon id="work" size="big" color="--color-primary" />
                              </template>

                              <template #containerEnd>
                                <Copy type="header" size="large" color="--color-primary">{{ header.name }}</Copy>
                              </template>
                            </KeyValuePair>
                          </template>

                          <template #byline>
                            <div style="display: flex; flex-direction: row; padding: 0.5rem 0rem; gap: 1rem">
                              <KeyValuePair direction="horizontal" renderAsLabel labelColorNamespace="accent">
                                <template #containerStart>
                                  <Copy type="label" size="small" :color="`--color-primary`">{{ t("PORTFOLIO_TYPE") }}</Copy>
                                </template>

                                <template #containerEnd>
                                  <Copy type="body" size="small" color="--color-primary"> {{ header.type }} </Copy>
                                </template>
                              </KeyValuePair>

                              <KeyValuePair direction="horizontal" renderAsLabel labelColorNamespace="accent">
                                <template #containerStart>
                                  <Copy type="label" size="small" color="--color-primary">{{ t("PORTFOLIO_ACQUIRED") }}</Copy>
                                </template>

                                <template #containerEnd>
                                  <Copy type="body" size="small" color="--color-primary"> {{ filter.date.short(header.acquisitionDate) }}</Copy>
                                </template>
                              </KeyValuePair>
                            </div>
                          </template>
                        </EntityCard>
                      </template>
                    </WidgetBlank>
                  </template>
                </WidgetLayoutRowCellSingle>

                <WidgetLayoutRowCellSingle>
                  <template #widget>
                    <WidgetBlank colorBackground="--color-white" colorForeground="--color-primary" omitPadding omitClickMore>
                      <template #container>
                        <UrlTabContainer :items="tabs" basePath="portfolio-portfolioId" context="portfolio" :params="{ portfolioId }" />
                      </template>
                    </WidgetBlank>
                  </template>
                </WidgetLayoutRowCellSingle>
              </template>
            </WidgetLayout>
          </template>
        </CompositionLayoutSingle>
      </template>
    </Composition>

    <Composition :class="[layoutStore.widgetScroll && $style.widgetScroll]">
      <template #layouts>
        <CompositionLayoutSingle omitPaddingTop>
          <template #container>
            <WidgetLayout outline>
              <template #widgetRows>
                <slot />
              </template>
            </WidgetLayout>
          </template>
        </CompositionLayoutSingle>
      </template>
    </Composition>
  </div>
</template>

<style module>
.container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.widgetScroll {
  overflow: hidden;
}
</style>
